import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Button,
  Dialog,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  BugReport as BugIcon,
  Lightbulb as IdeaIcon,
  ChatBubbleOutline as OtherIcon,
} from '@mui/icons-material';
import { adminApi } from '../services/adminApi';

const ADMIN_API = `${process.env.REACT_APP_EXPRESS_API_URL || 'https://famelia-admin-api-web.azurewebsites.net'}/api/admin`;

interface FeedbackEntry {
  id: string;
  user_id: string | null;
  user_name: string;
  category: string;
  message: string;
  screenshot_url: string | null;
  app_version: string | null;
  platform: string | null;
  screen: string | null;
  created_at: string;
}

const CATEGORY_META: Record<string, { color: 'error' | 'success' | 'default'; icon: React.ReactNode; label: string }> = {
  bug: { color: 'error', icon: <BugIcon fontSize="small" />, label: 'Bug' },
  idea: { color: 'success', icon: <IdeaIcon fontSize="small" />, label: 'Idea' },
  other: { color: 'default', icon: <OtherIcon fontSize="small" />, label: 'Other' },
};

export function FeedbackTab() {
  const [feedback, setFeedback] = useState<FeedbackEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<'all' | 'bug' | 'idea' | 'other'>('all');
  const [zoomUrl, setZoomUrl] = useState<string | null>(null);

  const authHeader = { Authorization: adminApi.getAuthToken() };

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${ADMIN_API}/feedback`, { headers: authHeader });
      setFeedback(res.data.feedback ?? []);
    } catch (err: any) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to load feedback.');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchFeedback(); }, [fetchFeedback]);

  const filtered = feedback.filter((f) => {
    if (category !== 'all' && f.category !== category) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      f.message.toLowerCase().includes(q) ||
      f.user_name.toLowerCase().includes(q) ||
      (f.screen || '').toLowerCase().includes(q)
    );
  });

  const counts = {
    bug: feedback.filter((f) => f.category === 'bug').length,
    idea: feedback.filter((f) => f.category === 'idea').length,
    other: feedback.filter((f) => f.category === 'other').length,
    shots: feedback.filter((f) => !!f.screenshot_url).length,
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>User Feedback</Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={fetchFeedback}>Refresh</Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
        <Chip label={`${feedback.length} total`} color="primary" variant="outlined" />
        <Chip label={`${counts.bug} bugs`} color="error" variant="outlined" />
        <Chip label={`${counts.idea} ideas`} color="success" variant="outlined" />
        <Chip label={`${counts.other} other`} variant="outlined" />
        <Chip label={`${counts.shots} with screenshot`} color="info" variant="outlined" />
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          placeholder="Search message, user, or screen…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 360 }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
        />
        <Stack direction="row" spacing={1}>
          {(['all', 'bug', 'idea', 'other'] as const).map((c) => (
            <Chip
              key={c}
              label={c === 'all' ? 'All' : CATEGORY_META[c].label}
              onClick={() => setCategory(c)}
              color={category === c ? 'primary' : 'default'}
              variant={category === c ? 'filled' : 'outlined'}
              size="small"
            />
          ))}
        </Stack>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Message</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Screenshot</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>From</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Where</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {search || category !== 'all' ? 'No matching feedback.' : 'No feedback yet.'}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((f) => {
                  const meta = CATEGORY_META[f.category] ?? CATEGORY_META.other;
                  return (
                    <TableRow key={f.id} hover>
                      <TableCell>
                        <Chip icon={meta.icon as any} label={meta.label} size="small" color={meta.color} variant="outlined" />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 420 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{f.message}</Typography>
                      </TableCell>
                      <TableCell>
                        {f.screenshot_url ? (
                          <Box
                            component="img"
                            src={f.screenshot_url}
                            alt="screenshot"
                            onClick={() => setZoomUrl(f.screenshot_url)}
                            sx={{ width: 44, height: 76, objectFit: 'cover', borderRadius: 1, cursor: 'pointer', border: '1px solid #eee' }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{f.user_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{f.screen || '—'}</Typography>
                        {f.app_version && (
                          <Typography variant="caption" color="text.disabled">v{f.app_version}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(f.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Screenshot lightbox */}
      <Dialog open={!!zoomUrl} onClose={() => setZoomUrl(null)} maxWidth="sm">
        {zoomUrl && (
          <Box
            component="img"
            src={zoomUrl}
            alt="screenshot"
            sx={{ maxWidth: '90vw', maxHeight: '85vh', display: 'block' }}
            onClick={() => setZoomUrl(null)}
          />
        )}
      </Dialog>
    </Box>
  );
}
